import { Fragment, useEffect, useRef, useState, useContext } from "react";
import { ADModalBody, AdvancedDragModal } from "src/componentsUtils/AdvancedDragModal";
import { useWindowContext } from "src/WindowManager/useWindowContext";
import { closeWindow, ManagedWindow } from "src/WindowManager/WindowState";
import { Localization, SettingsManager } from "@viamap/viamap2-common";
import { GlassFoldUdBox, KeyValueLine, KeyValuesBoxGrid, KeyValuesHorizontal, PropertyTextDoc, ValueGroup, ViewButtonIcon, ViewButtonText } from "./PropertyComponents";
import { GlassButton } from "src/components/GlassButtons";
import { PropertyData } from "./PropertyInfoCollectorTypes";
import { getTingbogsDokLink, TingBogRecordNumber } from "./PropertyInfoGroupFormatters";
import { Spinner } from "react-bootstrap";
import { GlassInfo } from "src/components/MitGlassComponents";
import { BiBug } from "react-icons/bi";
import { BsFileEarmarkPdf } from "react-icons/bs";
import { Persistence } from "src/managers/Persistence";
import { MapitStateContext, MapitStateActionType } from "src/states/MapitState";
import { PropertyInfoInterface } from 'src/managers/PropertyInfoInterface';
import { parse as WKTparse } from 'wkt';
import { GenerateGeomUtils } from 'src/managers/GenerateGeomUtils';
import { BFELookup } from 'src/managers/SearchBarGeneralPlugin';
import polylabel from 'polylabel';
import { DC } from "src/dataCollector/DataCollector";
import { message } from "src/components/Message";
import { ServitutAnnotationsTable } from "src/modules/ANNModule/ANNComponents";
import { ANNLLMAnalyzer } from "src/modules/ANNModule/ANNLLMAnalyzer";
import { ProtectedFeature } from "src/components/ProtectedFeature";
import { Feature } from "src/states/ApplicationStateFeatures";
import { BilagsVisning } from "./SharedTLViewComponents";

export function ServitutDetailsView(props: { managedWindow: ManagedWindow }) {
   const { dispatch: windowDispatch } = useWindowContext();
   type ViamapTingBog = NonNullable<PropertyData["ViamapTingBog"]>;
   type Servitut = ViamapTingBog["servitutter"][number] & { dokumentFilnavn?: string };
   type StamOplysninger = ViamapTingBog["stamOplysninger"];

   const servitut = props.managedWindow.windowParams?.servitut as Servitut | undefined;
   const stamOplysninger = props.managedWindow.windowParams?.stamOplysninger as StamOplysninger | undefined;
   const viamapTingBog = props.managedWindow.windowParams?.viamapTingBog as ViamapTingBog | undefined;
   const propertyData = props.managedWindow.windowParams?.propertyData as PropertyData | undefined;
   console.log("servitut", servitut);

   const [loading, setLoading] = useState(false);
   const [error, setError] = useState<string | null>(null);
   const [xmlDoc, setXmlDoc] = useState<Document | null>(null);
   const [downloadingPdf, setDownloadingPdf] = useState(false);

   async function fetchServitutDetailsByUuid(uuid: string, abortSignal: AbortSignal) {
      setLoading(true);
      setError(null);
      DC.TL.DokumentTingbog(uuid).then((data) => {
         if (abortSignal.aborted) return;
         setXmlDoc(data);
      }).catch((err) => {
         if (abortSignal.aborted) return;
         setXmlDoc(null);
         setError(err.message || String(err));
      }).finally(() => {
         setLoading(false);
      });
   }

   useEffect(() => {
      if (!servitut?.dokumentId) return;
      let abort = new AbortController();
      fetchServitutDetailsByUuid(servitut.dokumentId, abort.signal);
      return () => {
         abort.abort();
      };
   }, [servitut?.dokumentId]);

   if (!servitut) {
      return (
         <AdvancedDragModal
            window={props.managedWindow}
            title={Localization.getText("Servitutdetails")}
            onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
            allowFullscreen={true}
            allowUserMiniMize={true}
            PosDefault={{ width: "800px", height: "520px", left: "10%", top: "10%" }}
         >
            <ADModalBody>
               <GlassInfo>
                  {Localization.getText("Missing context for servitut details") || "Missing context for servitut details"}
               </GlassInfo>
            </ADModalBody>
         </AdvancedDragModal>
      );
   }

   let servitutTingbogLink = getTingbogsDokLink(servitut);
   const annotationDocumentId = servitut?.dokumentId || servitut?.aktAliasId || "";
   const aktIdForDatoloebenr = (servitut?.aktAliasId && servitut.aktAliasId !== "") ? servitut.aktAliasId : servitut?.aktId;
   const datoloebenr = aktIdForDatoloebenr ? TingBogRecordNumber(aktIdForDatoloebenr) : undefined;

   // Helper function to check if a servitut is digital (after 2009)
   // datoloebenr format: DDMMYYYY-XXXXX
   const isServitutDigital = (datoloebenr?: string): boolean => {
      if (!datoloebenr) return false;
      // Extract year from DDMMYYYY-XXXXX format (positions 4-7)
      const yearMatch = datoloebenr.match(/^(\d{2})(\d{2})(\d{4})-/);
      if (yearMatch) {
         const year = parseInt(yearMatch[3], 10);
         return year > 2009;
      }
      return false;
   };

   // Download endpoint should only be used when we have the actual scanned akt id.
   // In Tingbog data this is represented by `dokumentFilnavn`.
   const aktIdForPdf = (servitut as any)?.dokumentFilnavn as string | undefined;
   const hasPdf = Boolean(aktIdForPdf);

   async function downloadPdf(pdfUrl?: string) {
      if (!pdfUrl || downloadingPdf) return;

      setDownloadingPdf(true);
      try {
         const analyzer = new ANNLLMAnalyzer();
         const blob = await analyzer.fetchPdf(pdfUrl);
         const url = window.URL.createObjectURL(blob);
         const link = document.createElement('a');
         link.href = url;
         link.download = `akt_${aktIdForPdf}.pdf`;
         document.body.appendChild(link);
         link.click();
         document.body.removeChild(link);
         window.URL.revokeObjectURL(url);
      } catch (err: any) {
         message.error(err.message || Localization.getText("Failed to download PDF"));
      } finally {
         setDownloadingPdf(false);
      }
   }

   return (
      <AdvancedDragModal
         window={props.managedWindow}
         title={Localization.getText("Servitutdetails")}
         onClose={() => windowDispatch(closeWindow(props.managedWindow.id))}
         allowFullscreen={true}
         allowUserMiniMize={true}
         PosDefault={{ width: "800px", height: "520px", left: "10%", top: "10%" }}
         subHeaderContent={
            <>
               {SettingsManager.getSystemSetting("debuggingShowExtraDebugging") ? (
                  <>
                     <ViewButtonIcon onClick={() => console.info(xmlDoc)} ><BiBug /><span style={{ paddingInline: "5px 5px" }} >Log to Console</span></ViewButtonIcon>
                  </>
               ) : null}
               {hasPdf && !isServitutDigital(datoloebenr) ? (
                  <ViewButtonIcon
                     title={Localization.getText("Download PDF")}
                     disabled={downloadingPdf}
                     onClick={() => {
                        void downloadPdf(aktIdForPdf);
                     }}
                  >
                     {downloadingPdf ? <Spinner animation="border" size="sm" role="status" style={{ marginRight: "5px" }} /> : <BsFileEarmarkPdf />}
                     <span style={{ paddingInline: "5px" }}>{downloadingPdf ? Localization.getText("Downloading...") : Localization.getText("Download PDF")}</span>
                  </ViewButtonIcon>
               ) : null}
               {!hasPdf && !isServitutDigital(datoloebenr) ? (
                  <ViewButtonIcon title={"Inskannet akt fra Ejendom"}
                     disabled={downloadingPdf}
                     onClick={() => {
                        let inskannetAktID = viamapTingBog?.stamOplysninger?.indskannetAktDokumentId;
                        downloadPdf(inskannetAktID)
                     }}
                  >
                     {downloadingPdf ? <Spinner animation="border" size="sm" role="status" style={{ marginRight: "5px" }} /> : <BsFileEarmarkPdf />}
                     <span style={{ paddingInline: "5px" }}>{downloadingPdf ? Localization.getText("Downloading...") : Localization.getText("Download PDF")}</span>
                  </ViewButtonIcon>
               ) : null}

               <ViewButtonIcon
                  title={servitutTingbogLink ? undefined : Localization.getText("No link available")}
                  disabled={!servitutTingbogLink}
                  onClick={() => {
                     window.open(servitutTingbogLink, "_blank");
                  }}
               >
                  <span style={{ paddingInline: "5px" }}>Åbn på Tinglysning.dk</span>
               </ViewButtonIcon>
            </>
         }
      >
         <ADModalBody >
            <ServitutHeader servitut={servitut} xmlDoc={xmlDoc} />
            {loading ? <ShowLoading link={servitutTingbogLink} /> : null}
            {error ? <ShowError /> : null}
            {xmlDoc ? (
               <>
                  <ShowTinglysningMessage xmlDoc={xmlDoc} link={servitutTingbogLink} />
                  <ServitutEjendomme servitutXDOC={xmlDoc} servitut={servitut} />
                  <ServitutTekst servitutXDOC={xmlDoc} />
                  <BilagsVisning servitutXDOC={xmlDoc} />
                  <ProtectedFeature feature={Feature.AnnotationModule} contentsIfNoAccess={<></>}>
                     <ServitutAnnotationsTable
                        documentId={annotationDocumentId}
                        datoloebenr={datoloebenr}
                        servitut={servitut}
                        stamOplysninger={stamOplysninger}
                        xmlDoc={xmlDoc}
                        viamapTingBog={viamapTingBog}
                        propertyData={propertyData}
                     />
                  </ProtectedFeature>
               </>
            ) : null}
         </ADModalBody>
      </AdvancedDragModal >
   );
}

function ShowLoading({ link }: { link?: string }) {
   let textRef = useRef<HTMLDivElement>(null);
   let [showQuickButton, setShowQuickButton] = useState(false);

   useEffect(() => {
      if (!textRef.current) return;
      const box = textRef.current;
      function updateText() {
         box.innerText = Localization.getText("The action takes longer than expected");
         setShowQuickButton(true);
      }
      const interval = setTimeout(() => updateText(), 5000);
      box.innerText = Localization.getText("Loading");
      return () => {
         clearTimeout(interval);
      }
   }, [textRef]);

   return (
      <GlassInfo>
         <div ref={textRef} style={{ padding: 8 }} />
         <Spinner animation="border" role="status" />
         {showQuickButton && link ? <><br /><ViewButtonText style={{ marginTop: "1em" }}
            onClick={() => {
               window.open(link, "_blank");
            }}

         >Åbn på Tinglysning.DK</ViewButtonText></> : null}
      </GlassInfo>
   )
}

function ShowError() {
   return (
      <GlassInfo>
         <div style={{ padding: 8, color: "var(--dangerColor)" }}>{Localization.getText("Failed to fetch data")}</div>
      </GlassInfo>
   )
}

function ShowTinglysningMessage({ xmlDoc, link }: { xmlDoc: Document | null, link?: string }) {
   if (!(xmlDoc instanceof Document) || xmlDoc.children[0]?.children.length !== 0) {
      return <></>
   }
   function modifyTLMessage(link?: string, text?: string) {
      let textReplacment = link ? "\nServitut del af ældre dokumentsamling. Benyt download pdf for at hente denne" : "\nServitutten kan muligvis findes i ejendommens øvrige indskannet akt. Benyt download pdf knappen for at hente denne.";
      if (text) {
         return text.replace("Benyt i stedet forespørgsel i tingbøgerne.", textReplacment).trim();
      }
   }

   return (
      <GlassInfo>
         {modifyTLMessage(link, xmlDoc?.children[0].textContent)}

      </GlassInfo>
   )
}

function ServitutHeader({ servitut, xmlDoc }: { servitut: (PropertyData["ViamapTingBog"] & {})["servitutter"][number], xmlDoc?: Document | null, originalTinglysningsDato?: string }) {

   const formatDate = (val?: string) => {
      return val ? new Date(val).toLocaleDateString(Localization.getLocale(), { year: 'numeric', month: '2-digit', day: '2-digit' }) : "";
   };

   const normalizeDatePart = (val?: string | null) => (val?.split("T")[0] || "");

   const documentAliasIdentifikator = (xmlDoc?.getElementsByTagName("DokumentAliasIdentifikator")[0]?.textContent?.split("-")[0]) || "";
   let originalDateOnly: string | undefined;
   if (/^\d{8}$/.test(documentAliasIdentifikator)) {
      const y = documentAliasIdentifikator.slice(0, 4), m = documentAliasIdentifikator.slice(4, 6), d = documentAliasIdentifikator.slice(6, 8);
      originalDateOnly = formatDate(`${y}-${m}-${d}`);
   } else {
      originalDateOnly = servitut.tinglysningsDato ? formatDate(normalizeDatePart(servitut.tinglysningsDato)) : undefined;
   }

   const lastPåtegnetRaw = xmlDoc?.getElementsByTagName("TinglysningsDato")[0]?.textContent || "";
   const lastPåtegnetDateOnly = formatDate(normalizeDatePart(lastPåtegnetRaw));

   const shouldShowLastPåtegnet = Boolean(lastPåtegnetRaw) && (originalDateOnly !== lastPåtegnetDateOnly);

   console.log("xml", originalDateOnly);
   console.log("xml", lastPåtegnetDateOnly);

   const aktIdForDisplay = (servitut?.aktAliasId && servitut.aktAliasId !== "") ? servitut.aktAliasId : servitut?.aktId;
   return (
      <KeyValuesHorizontal >
         <KeyValueLine valKey={Localization.getText("Date/serial number")} value={aktIdForDisplay ? TingBogRecordNumber(aktIdForDisplay) : ""} />
         {shouldShowLastPåtegnet ? <KeyValueLine valKey={<span style={{ color: "var(--dangerColor)" }}>{Localization.getText("Last signed")}</span>} value={lastPåtegnetDateOnly} /> : null}
      </KeyValuesHorizontal >
   )
}

function ServitutEjendomme({ servitutXDOC, servitut }: { servitutXDOC: Document | null, servitut: any }) {
   const { dispatch: mapitStateDispatch } = useContext(MapitStateContext);
   const [creatingLayer, setCreatingLayer] = useState(false);
   if (!(servitutXDOC instanceof Document)) {
      return <></>
   }

   let Ejendomme = Array.from(servitutXDOC.getElementsByTagName("FastEjendomMedRettigheder"));
   if (Ejendomme.length === 0) {
      return <></>
   }

   async function handleAddAsLayer() {
      setCreatingLayer(true);
      try {
         const polygonFeatures: any[] = [];
         const markerFeatures: any[] = [];
         const missingAddressBFEs: string[] = [];
         const bfeLookupInstance = new BFELookup(() => { });

         for (const ejendom of Ejendomme) {
            const bfe = ejendom.getElementsByTagName("BestemtFastEjendomNummer")[0]?.textContent || null;
            if (!bfe) {
               throw new Error('Servitut: missing BFE number in FastEjendomMedRettigheder element');
            }

            const aktIdForLabel = (servitut?.aktAliasId && servitut.aktAliasId !== "") ? servitut.aktAliasId : servitut?.aktId;
            const servitutLabel = aktIdForLabel ? TingBogRecordNumber(aktIdForLabel) : "";
            const baseProps: any = {
               servitutId: servitut?.dokumentId,
               servitutdatoløbenummer: servitutLabel,
               BFEnummer: bfe
            };

            // Adds polygons and markers to their lists for making layers later.
            const idNum = Number(bfe);
            if (!Number.isNaN(idNum)) {
               const suggestions = await bfeLookupInstance.calculateSuggestions({}, bfe.toString());
               if (suggestions.length === 1) {
                  const bfeWithoutSFE = suggestions.filter(s => s?.data?.type === 'Ejerlejlighed' || s?.data?.type === 'BPFG');
                  if (bfeWithoutSFE.length === 0) { //SFE
                     try {
                        const sfeGeo = await PropertyInfoInterface.getGeojsonOfSFE(idNum);
                        const sfeFeatures = (sfeGeo && Array.isArray(sfeGeo.features)) ? sfeGeo.features : [];
                        for (const sf of sfeFeatures) {
                           if (sf?.geometry && (sf.geometry.type === 'Polygon' || sf.geometry.type === 'MultiPolygon')) {
                              polygonFeatures.push({ type: 'Feature', geometry: sf.geometry, properties: { ...baseProps, ...(sf.properties || {}) } });
                           }
                        }
                     } catch (e) {
                        console.warn('Servitut: SFE polygon fetch error (skipping polygons) for', bfe, e);
                     }
                  }
                  else { //Ejerlejighed/BPFG
                     const coords = await handleCoordinates(bfeWithoutSFE[0].data);
                     if (!coords) {
                        missingAddressBFEs.push(bfe);
                        continue;
                     }
                     const propsBase = { BFEnummer: bfe };
                     markerFeatures.push({
                        type: 'Feature',
                        geometry: { type: 'Point', coordinates: [coords.lng, coords.lat] },
                        properties: { ...propsBase, Ejendomstype: bfeWithoutSFE[0].data.type }
                     });
                  }
               }
               else {
                  throw new Error('Servitut: multiple BFELookup suggestions for BFE number in FastEjendomMedRettigheder element: ' + bfe);
               }
            } else {
               throw new Error('Servitut: invalid BFE number in FastEjendomMedRettigheder element: ' + bfe);
            }
         }

         // Inform the user about BFEs without address, but continue with layers for others
         if (missingAddressBFEs.length > 0) {
            message.info(Localization.getText("One or more properties have no address. BFE") + ": " + missingAddressBFEs.join(', '));
         }

         if (polygonFeatures.length === 0 && markerFeatures.length === 0) {
            message.error('no geometries found (no polygons, no points)');
            return;
         }

         const aktIdForDatasetLabel = (servitut?.aktAliasId && servitut.aktAliasId !== "") ? servitut.aktAliasId : servitut?.aktId;
         const label = aktIdForDatasetLabel ? TingBogRecordNumber(aktIdForDatasetLabel) : "";
         const baseDatasetName = Localization.getText("Record") + ` ${label} ` + Localization.getText("registered");

         if (polygonFeatures.length > 0) {
            const polyFc = { type: 'FeatureCollection', features: polygonFeatures };
            Persistence.createLayersFromGeoJSON(`${baseDatasetName} - polygoner`, polyFc as any, new Date(), (li: any) => {
               mapitStateDispatch({ type: MapitStateActionType.AddDataLayer, payload: { layerInfo: li, zoomToFocus: false } });
            });
         }

         if (markerFeatures.length > 0) {
            const markerFc = { type: 'FeatureCollection', features: markerFeatures };
            Persistence.createLayersFromGeoJSON(`${baseDatasetName} - punkter`, markerFc as any, new Date(), (li: any) => {
               mapitStateDispatch({ type: MapitStateActionType.AddDataLayer, payload: { layerInfo: li, zoomToFocus: false } });
            });
         }

      } catch (err: any) {
         message.error(err.message || String(err));
      } finally {
         setCreatingLayer(false);
      }
   }

   return (
      <GlassFoldUdBox
         title={
            <>
               <span style={{ marginRight: 'auto' }}>{Localization.getText("Properties") + " (" + Ejendomme.length + ")"}</span>
               {creatingLayer ? <Spinner animation="border" size="sm" role="status" style={{ marginRight: 8 }} /> : null}
               <GlassButton
                  disabled={creatingLayer}
                  onMouseDown={(e: any) => { e.stopPropagation(); }}
                  onClick={(e: any) => { e.stopPropagation(); void handleAddAsLayer(); }}
               >
                  {Localization.getText("Add as layer")}
               </GlassButton>
            </>
         }
         foldetUd={false}>
         {Ejendomme.map((ejendom, index) => (
            <Fragment key={index}>
               <ValueGroup>
                  <KeyValueLine valKey={"BFEnummer"} value={ejendom.getElementsByTagName("BestemtFastEjendomNummer")[0]?.textContent || ""} />
                  <KeyValuesBoxGrid minWidth={"150px"} >
                     {Array.from(ejendom.getElementsByTagName("Matrikel")).map((matrikel, idx) => {
                        const name = matrikel.getElementsByTagName("CadastralDistrictName")[0]?.textContent || "";
                        const matrNr = String(matrikel.getElementsByTagName("Matrikelnummer")[0]?.textContent ?? "")?.replace(/^0*/, "");
                        return (
                           <Fragment key={idx}>
                              <KeyValueLine valKey={"Matnr. " + matrNr} value={name} keepEmpty />
                           </Fragment>
                        )
                     })}
                  </KeyValuesBoxGrid>
               </ValueGroup>
            </Fragment>
         ))}
      </GlassFoldUdBox>
   )
}

function ServitutTekstGrupper({ servitutXDOC }: { servitutXDOC: Element }) {
   let header = servitutXDOC.getElementsByTagName("Overskrift")[0];
   let paragraphs = servitutXDOC.getElementsByTagName("Afsnit");
   return (<>
      {header ? (
         <h1>
            {header.textContent}
         </h1>
      ) : null}
      {[...paragraphs].map((p, idx) => (
         <p key={idx} >
            {p.textContent}
         </p>
      ))}
   </>
   )
}

function ServitutTekst({ servitutXDOC }: { servitutXDOC: Document | null }) {
   if (!(servitutXDOC instanceof Document)) {
      return <></>
   }
   let servitut = servitutXDOC.getElementsByTagName?.("Servitut")[0];
   let servitutTextGroups = [...servitut?.getElementsByTagName("TekstGruppe") || []];
   if (servitutTextGroups.length === 0) {
      return <></>
   }
   let firstTextGroup = servitutTextGroups[0];
   let header = firstTextGroup.getElementsByTagName("Overskrift")[0];
   let paragraphs = firstTextGroup.getElementsByTagName("Afsnit");
   return (
      <GlassFoldUdBox title={"Servitut - " + (header?.textContent || paragraphs[0]?.textContent)} foldetUd={false}>
         <PropertyTextDoc>
            {servitutTextGroups.map((tg, idx) => {
               return (
                  <Fragment key={idx}>
                     {(tg.children.length > 1 || idx != 0) && (
                        <ServitutTekstGrupper key={idx} servitutXDOC={tg} />
                     )}
                  </Fragment>
               )
            })}
         </PropertyTextDoc>
      </GlassFoldUdBox>
   )
}


const handleCoordinates = async (data: {
   BFEnummer: number,
   SFEnummer?: string | number, // finde center of sfe, 
   type: "SFE" | "BPFG" | "Ejerlejlighed",
   geometri?: string, //WKT string
}): Promise<any> => {
   if (data.geometri) {
      let tmp = WKTparse(data.geometri)
      if (tmp?.coordinates[0]) {
         let coords = GenerateGeomUtils.convertFromUTMtoLatLng({ x: tmp.coordinates[0], y: tmp.coordinates[1] })
         return coords;
      }
   }
   if ("geometri" in data) {
      delete data.geometri;
   }
   if (data.type) {
      let EBR = await DC.DF_GQL.EBR.EBRAdresseOgPunkt(String(data.BFEnummer));
      let geometri = EBR[0]?.geometri;
      if (geometri) {
         let tmp = WKTparse(geometri)
         if (tmp?.coordinates[0]) {
            let coords = GenerateGeomUtils.convertFromUTMtoLatLng({ x: tmp.coordinates[0], y: tmp.coordinates[1] })
            return coords;
         }
      }
   }
   if (data.type == "SFE") {
      let geoJson = await PropertyInfoInterface.getGeojsonOfSFE(data.BFEnummer);
      let coords = polylabel(geoJson.features[0].geometry.coordinates, 0.00001)
      return coords;
   }
};
